import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  PaperClipOutlined,
  PlusOutlined,
  RedoOutlined,
  StarFilled,
  StarOutlined,
  StopOutlined
} from '@ant-design/icons';
import {
  App,
  Button,
  Card,
  Col,
  FloatButton,
  Grid,
  Input,
  Layout,
  Modal,
  Rate,
  Row,
  Space,
  Spin,
  Tag,
  theme,
  Tooltip,
  Typography
} from 'antd';
import { Content } from 'antd/es/layout/layout';
import { collection, onSnapshot, query, Timestamp } from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useDispatch, useSelector } from 'react-redux';
import { ask, createChatMessage, createChatRoom, getMessages, getUser, ratePrompt } from '../../apis/apis';
import AppHeader from '../../components/layout/AppHeader';
import { auth, db, storage } from '../../config/firebase';
import { AIProvider, AskMessage, ChatMessage, ChatRoom, User } from '../../models/models';
import { setPromptSendMessageData } from '../../redux/slices/promptSendMessageSlice';
import { RootState } from '../../redux/store';
import { convertTimestampToDate } from '../../utils/functions';

import { useTranslation } from "react-i18next";


const STREAM_TIMEOUT_MS = 120_000;
const { Text } = Typography;
const { useToken } = theme;

type AttachmentUI = {
  name: string;
  contentType: string;
  size: number;
  gcsPath?: string;
  downloadURL?: string;
  uploading?: boolean;
  error?: string;
};

const ACCEPT = ['image/*', '.pdf', '.csv', '.txt', '.docx', '.xls', '.xlsx'].join(',');
const MAX_SIZE_MB = 20;
const MAX_FILES = 5;

const ChatPageContent: React.FC = () => {
  const { message, modal } = App.useApp();
  const chatRoomState = useSelector((state: RootState) => state.chatRoom);
  const promptSendMessageDataState = useSelector((state: RootState) => state.promptSendMessageData);
  const { token } = useToken();
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);

  const [loading, setLoading] = useState(false);
  const [isAsking, setIsAsking] = useState(false);
  const [askController, setAskController] = useState<AbortController>(new AbortController());

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [model, setModel] = useState<string | null>(null);
  const [chatRoom, setChatRoom] = useState<ChatRoom | null>(null);
  const [aiModels, setAiModels] = useState<{ value: string; label: string }[]>([]);
  const [curretUser, setCurrentUser] = useState<User | null>(null);

  const [attachments, setAttachments] = useState<AttachmentUI[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<any>(null);
  const [textLines, setTextLines] = React.useState(1);

  const [isRateModalVisible, setIsRateModalVisible] = useState(false);
  const [currentRate, setCurrentRate] = useState(0);
  const [currentRatedMessageId, setCurrentRatedMessageId] = useState<string>('');

  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const autoStickRef = useRef(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const lastAutoScrollAtRef = useRef(0);
  const isAskingRef = useRef(false);
  const dispatch = useDispatch();
  const skipResetAttachmentsRef = useRef(false);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [composerBox, setComposerBox] = useState<{ left: number; width: number }>({ left: 0, width: 0 });
  const composerRef = useRef<HTMLDivElement | null>(null);
  const [composerHeight, setComposerHeight] = useState<number>(100);
  const [kbOverlap, setKbOverlap] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(64);

  const isEmpty = !messages || messages.length === 0;

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const el = textareaRef.current?.resizableTextArea?.textArea as HTMLTextAreaElement | undefined;
    if (!el) return;
    const cs = window.getComputedStyle(el);
    const lh = parseFloat(cs.lineHeight || '0') || 0;
    const pt = parseFloat(cs.paddingTop || '0') || 0;
    const pb = parseFloat(cs.paddingBottom || '0') || 0;
    const inner = el.scrollHeight - pt - pb;
    const lns = Math.max(1, Math.round(inner / (lh || 1)));
    setTextLines(lns);
  }, []);

  useLayoutEffect(() => {
    const update = () => {
      const el = chatContainerRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setComposerBox({ left: r.left, width: r.width });
    };
    update();
    const ro = new ResizeObserver(update);
    if (chatContainerRef.current) ro.observe(chatContainerRef.current);
    ro.observe(document.body);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    if (!composerRef.current) return;
    const ro = new ResizeObserver(() => setComposerHeight(composerRef.current?.offsetHeight || 80));
    ro.observe(composerRef.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const vv = window.visualViewport;
    const setVvh = () => {
      const h = vv ? vv.height : window.innerHeight;
      document.documentElement.style.setProperty('--vvh', `${Math.round(h)}px`);
    };
    const onChange = () => {
      const overlap = Math.max(
        0,
        window.innerHeight - ((vv?.height || window.innerHeight) + (vv?.offsetTop || 0))
      );
      setKbOverlap(overlap);
      setVvh();
      if (overlap > 0) {
        autoStickRef.current = true;
        stickToBottomIfNeeded('smooth');
      }
    };
    onChange();
    vv?.addEventListener('resize', onChange);
    vv?.addEventListener('scroll', onChange);
    window.addEventListener('resize', onChange);
    return () => {
      vv?.removeEventListener('resize', onChange);
      vv?.removeEventListener('scroll', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);


  useEffect(() => {
    const rootEl = scrollRef.current;
    const target = bottomRef.current;
    if (!rootEl || !target || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        const visible = !!e?.isIntersecting;
        setIsAtBottom(visible);
        autoStickRef.current = visible;
      },
      {
        root: rootEl,
        threshold: 0.1,
        rootMargin: `0px 0px -${composerHeight + 16}px 0px`
      }
    );

    io.observe(target);
    return () => io.disconnect();
  }, [composerHeight, kbOverlap]);

  useEffect(() => { isAskingRef.current = isAsking; }, [isAsking]);

  const makeTitleFrom = (txt: string) => {
    const cleaned = (txt || '').replace(/\s+/g, ' ').trim();
    if (!cleaned) return t('chat.newChat');
    return cleaned.length <= 36 ? cleaned : cleaned.slice(0, 36).trimEnd() + '...';
  };

  function guessContentTypeFromName(name: string): string {
    const ext = (name.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'csv': return 'text/csv';
      case 'txt': return 'text/plain';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'png': return 'image/png';
      case 'jpg':
      case 'jpeg': return 'image/jpeg';
      case 'webp': return 'image/webp';
      case 'json': return 'application/json';
      default: return 'application/octet-stream';
    }
  }

  const scrollToBottom = (behavior: ScrollBehavior = 'auto') => {
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => el.scrollTo({ top: el.scrollHeight, behavior }));
  };

  const stickToBottomIfNeeded = (behavior: ScrollBehavior = 'auto') => {
    if (isAskingRef.current) {
      autoStickRef.current = true;
      scrollToBottom(behavior);
      return;
    }
    if (autoStickRef.current) scrollToBottom(behavior);
  };

  const throttledStickDuringStream = () => {
    const now = performance.now();
    if (now - lastAutoScrollAtRef.current > 80) {
      lastAutoScrollAtRef.current = now;
      stickToBottomIfNeeded('smooth');
    }
  };

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    if (isAskingRef.current) {
      autoStickRef.current = true;
      setIsAtBottom(true);
      return;
    }
    const nearBottom = el.scrollHeight - (el.scrollTop + el.clientHeight) < 24;
    setIsAtBottom(nearBottom);
    autoStickRef.current = nearBottom;
  };

  useLayoutEffect(() => {
    stickToBottomIfNeeded('smooth');
  }, [messages.length]);

  useEffect(() => {
    (async () => {
      const user = await getUser();
      setCurrentUser(user);
    })();
  }, []);

  useEffect(() => {
    if (!curretUser?.clientId) return;
    const providersRef = collection(db, `Clients/${curretUser.clientId}/AIProviders`);
    const q = query(providersRef);
    const unsub = onSnapshot(q, (snap) => {
      const providers = snap.docs.map((d) => ({ id: d.id, ...d.data() })) as AIProvider[];
      if (!providers.length) {
        message.error(t('chat.noAiModelError'));
        return;
      }
      const opts = providers.map((p) => ({ value: p.name, label: p.name }));
      setAiModels(opts);
      setModel((prev) => prev || providers[0].name);
    });
    return () => unsub();
  }, [curretUser, message]);

  useEffect(() => {
    setChatRoom(chatRoomState);
    setMessages([]);
    if (!skipResetAttachmentsRef.current) setAttachments([]);
    autoStickRef.current = true;
  }, [chatRoomState]);

  useEffect(() => {
    if (!chatRoom) return;
    getAllMessages();
  }, [chatRoom]);

  useEffect(() => {
    const { promptId, prompt, model: selectedModel, attachments, chatRoomId: intendedChatId } = promptSendMessageDataState;
    if (!selectedModel) setModel('ChatGPT'); else setModel(selectedModel);
    if (!prompt || !promptId || !chatRoom?.id || !selectedModel) return;
    if (intendedChatId && intendedChatId !== chatRoom.id) return;
    handlePromptSendMessage(prompt, promptId, selectedModel || 'ChatGPT', attachments || []);
    dispatch(setPromptSendMessageData({ promptId: '', prompt: '', chatRoomId: '', model: '', attachments: [] }));
  }, [promptSendMessageDataState, chatRoom?.id]);

  const openRateModal = (messageId: string) => {
    setCurrentRatedMessageId(messageId);
    setIsRateModalVisible(true);
  };

  const handleRatePrompt = async () => {
    if (!(currentRate > 0 && currentRatedMessageId && chatRoom?.id)) {
      message.error(t('chat.chooseRate'));
      return;
    }
    try {
      setLoading(true);
      const ok = await ratePrompt(currentRate, chatRoom.id, currentRatedMessageId);
      if (ok) {
        message.success(t('chat.thanksRate'));
        modal.info({
          title: t('chat.thanks'),
          content: t('chat.ratingSuccessfullySent'),
          onOk() { setIsRateModalVisible(false); },
        });
      } else {
        message.error(t('chat.promptNoLongerExists'));
      }
      setIsRateModalVisible(false);
      await getAllMessages();
    } catch {
      message.error(t('chat.promptNoLongerExists'));
    } finally {
      setLoading(false);
    }
  };

  const isUploading = attachments.some((a) => a.uploading);
  const handleAttachClick = () => fileInputRef.current?.click();

  const guessIcon = (ct: string) => {
    const t = (ct || '').toLowerCase();
    if (t.startsWith('image/')) return <FileImageOutlined />;
    if (t.includes('pdf')) return <FilePdfOutlined />;
    if (t.includes('sheet') || t.includes('excel')) return <FileExcelOutlined />;
    if (t.includes('wordprocessingml.document')) return <FileWordOutlined />;
    if (t.includes('json') || t.includes('csv') || t.startsWith('text/')) return <FileTextOutlined />;
    return <PaperClipOutlined />;
  };

  const validateFile = (file: File) => {
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_SIZE_MB) return t('chat.fileExceeds', { max: MAX_SIZE_MB });
    return null;
  };

  const uploadSingle = async (file: File, targetChatId?: string) => {
    const chatId = targetChatId ?? chatRoom?.id;
    if (!auth.currentUser || !curretUser?.clientId || !chatId) {
      message.error(t('chat.noSessionOrChat'));
      return null;
    }
    const err = validateFile(file);
    if (err) {
      message.error(`${file.name}: ${err}`);
      return null;
    }
    const safeName = file.name.replace(/\s+/g, '_');
    const path = `Clients/${curretUser.clientId}/Users/${auth.currentUser.uid}/ChatRooms/${chatId}/attachments/${Date.now()}_${safeName}`;
    const ref = storageRef(storage, path);
    const ct = file.type || guessContentTypeFromName(file.name);
    try {
      await uploadBytes(ref, file, { contentType: ct });
      const url = await getDownloadURL(ref);
      return { name: file.name, contentType: ct, size: file.size, gcsPath: path, downloadURL: url, uploading: false } as AttachmentUI;
    } catch {
      message.error(`${t('chat.uploadError')} ${file.name}`);
      return { name: file.name, contentType: ct, size: file.size, uploading: false, error: 'upload-failed' } as AttachmentUI;
    }
  };

  const onFilesChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    if (attachments.length + files.length > MAX_FILES) message.warning(t('chat.maxFilesWarning', { max: MAX_FILES }));
    const slot = MAX_FILES - attachments.length;
    const take = files.slice(0, Math.max(0, slot));

    let effectiveChat = chatRoom;
    if (!effectiveChat?.id) {
      if (!auth.currentUser) {
        message.error(t('chat.noActiveAccount'));
        return;
      }
      const titleSeed = take[0]?.name || inputValue || t('chat.newChat');
      const newChat: ChatRoom = { id: '', name: makeTitleFrom(titleSeed), userId: auth.currentUser.uid, createdAt: null };
      skipResetAttachmentsRef.current = true;
      const chatRoomId = await createChatRoom(newChat);
      if (!chatRoomId) {
        skipResetAttachmentsRef.current = false;
        message.error(t('chat.chatCreationError'));
        return;
      }
      newChat.id = chatRoomId;
      effectiveChat = newChat;
      setChatRoom(newChat);
      dispatch({ type: 'chatRoom/setChatRoom', payload: { ...newChat, createdAt: null } });
    }

    setAttachments((prev) => [
      ...prev,
      ...take.map((f) => ({ name: f.name, contentType: f.type || 'application/octet-stream', size: f.size, uploading: true })),
    ]);

    for (const f of take) {
      const uploaded = await uploadSingle(f, effectiveChat!.id);
      setAttachments((prev) => {
        const idx = prev.findIndex((a) => a.uploading && a.name === f.name && a.size === f.size);
        if (idx === -1) return prev;
        const copy = [...prev];
        copy[idx] = uploaded || { ...copy[idx], uploading: false, error: 'upload-failed' };
        return copy;
      });
    }
    skipResetAttachmentsRef.current = false;
  };

  const removeAttachment = async (idx: number) => {
    const att = attachments[idx];
    setAttachments((prev) => prev.filter((_, i) => i !== idx));
    if (att?.gcsPath) {
      try {
        const ref = storageRef(storage, att.gcsPath);
        await deleteObject(ref);
      } catch { }
    }
  };

  const handleSendMessage = async () => {
    dispatch(setPromptSendMessageData({ promptId: '', prompt: '', chatRoomId: '', model: model, attachments: [] }));
    if (isAsking) return;
    if (isUploading) return message.info(t('chat.waitForUploads'));
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    const content = inputValue.trim();
    if (!content && attachments.length === 0) return;
    const wireAttachments = attachments
      .filter((a) => a.gcsPath && a.downloadURL)
      .map((a) => ({ gcsPath: a.gcsPath!, downloadURL: a.downloadURL!, contentType: a.contentType, name: a.name, size: a.size }));
    let effectiveChat = chatRoom;
    if (!effectiveChat?.id) {
      const newChat: ChatRoom = { id: '', name: makeTitleFrom(content || (wireAttachments[0]?.name ?? t('chat.newChat'))), userId: auth.currentUser.uid, createdAt: null };
      const chatRoomId = await createChatRoom(newChat);
      if (!chatRoomId) {
        message.error(t('chat.chatCreationError'));
        return;
      }
      newChat.id = chatRoomId;
      effectiveChat = newChat;
    }
    const chatMessage: ChatMessage & any = {
      id: '',
      userId: auth.currentUser.uid,
      chatRoomId: effectiveChat.id,
      content,
      owner: 'user',
      model: model!,
      createdAt: Timestamp.now(),
      fromPrompt: false,
      promptId: null,
      rated: false,
      role: 'user',
      prompt: null,
      attachments: wireAttachments,
    };
    setIsAsking(true);
    try {
      const savedMessageId = await createChatMessage(chatMessage);
      chatMessage.id = savedMessageId || '';
      setMessages((prev) => [...prev, chatMessage]);
      autoStickRef.current = true;
      scrollToBottom('smooth');
      await handleAsk(content, wireAttachments, effectiveChat.id, effectiveChat);
    } finally {
      setInputValue('');
      setAttachments([]);
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    }
  };

  const handlePromptSendMessage = async (prompt: string, promptId: string, modelUsed: string, wireAttachments: any[] = []) => {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    if (!chatRoom?.id) return;
    const chatMessage: ChatMessage & any = {
      id: '',
      userId: auth.currentUser.uid,
      chatRoomId: chatRoom.id,
      content: prompt,
      owner: 'user',
      model: modelUsed,
      createdAt: Timestamp.now(),
      fromPrompt: true,
      promptId,
      rated: false,
      role: 'user',
      prompt: null,
      attachments: wireAttachments,
    };
    const savedMessageId = await createChatMessage(chatMessage);
    chatMessage.id = savedMessageId || '';
    setMessages((prev) => {
      const next = [...prev, chatMessage];
      return next.filter((v, i, a) => a.findIndex((t) => t.content === v.content) === i);
    });
    autoStickRef.current = true;
    scrollToBottom('smooth');
    handleAskFromPrompt(prompt, promptId, modelUsed);
  };

  const getAllMessages = async () => {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    if (!chatRoom) return message.error(t('chat.noChatRoomSelected'));
    setLoading(true);
    try {
      const chatMessages = await getMessages(chatRoom);
      setMessages(chatMessages);
    } catch {
      message.error(t('chat.errorGettingMessages'));
    } finally {
      setLoading(false);
      autoStickRef.current = true;
      setTimeout(() => scrollToBottom('smooth'), 0);
    }
  };

  const handleAsk = async (userMessageContent: string, wireAttachments: any[] = [], chatRoomId?: string, newChat?: ChatRoom) => {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    setLoading(true);
    const controller = new AbortController();
    setAskController(controller);
    const user = await getUser();
    const clientId = user?.clientId;
    if (!clientId) {
      setLoading(false);
      return message.error(t('chat.noClientFound'));
    }
    const effectiveChatRoomId = chatRoomId || chatRoom?.id!;
    const askMessage: AskMessage & any = {
      id: '',
      clientId,
      userId: auth.currentUser.uid,
      chatRoomId: effectiveChatRoomId,
      content: userMessageContent,
      model: model!,
      createdAt: Timestamp.now(),
      temperature: 0.7,
      regenerate: false,
      attachments: wireAttachments,
    };
    let newMessage: ChatMessage & any = {
      id: 'temp-id',
      userId: auth.currentUser.uid,
      chatRoomId: effectiveChatRoomId,
      content: '',
      owner: 'ai',
      model: model!,
      createdAt: Timestamp.now(),
      fromPrompt: false,
      promptId: null,
      rated: false,
      role: 'assistant',
      prompt: userMessageContent,
      attachments: [],
    };
    setIsAsking(true);
    setMessages((prev) => [...prev, newMessage]);
    stickToBottomIfNeeded('smooth');
    const markerStart = '__OUTPUTS_JSON__';
    const markerEnd = '__END__';
    let outputsFromStream: any[] = [];
    let buffer = '';
    let outputsParsed = false;
    let finalText = '';
    const applyText = (t: string) => {
      if (!t) return;
      finalText += t;
      setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, content: m.content + t } : m)));
      throttledStickDuringStream();
    };
    const flushRemainder = () => {
      if (!buffer) return;
      if (!outputsParsed) {
        const sIdx = buffer.indexOf(markerStart);
        if (sIdx === -1) {
          applyText(buffer);
        } else {
          const before = buffer.slice(0, sIdx);
          applyText(before);
          const after = buffer.slice(sIdx + markerStart.length);
          const eIdx = after.indexOf(markerEnd);
          if (eIdx !== -1) {
            const jsonStr = after.slice(0, eIdx);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            const tail = after.slice(eIdx + markerEnd.length);
            if (tail) applyText(tail);
          }
        }
      } else {
        applyText(buffer);
      }
      buffer = '';
    };
    let timeoutId: any;
    try {
      timeoutId = setTimeout(() => {
        controller.abort();
        message.error(t('chat.timedOut'));
      }, STREAM_TIMEOUT_MS);
      await ask(
        askMessage,
        (chunk) => {
          buffer += chunk;
          while (true) {
            if (outputsParsed) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const sIdx = buffer.indexOf(markerStart);
            if (sIdx === -1) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const before = buffer.slice(0, sIdx);
            applyText(before);
            buffer = buffer.slice(sIdx + markerStart.length);
            const eIdx = buffer.indexOf(markerEnd);
            if (eIdx === -1) break;
            const jsonStr = buffer.slice(0, eIdx);
            buffer = buffer.slice(eIdx + markerEnd.length);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            outputsParsed = true;
          }
        },
        controller.signal
      );
      flushRemainder();
      newMessage.content = finalText;
      if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
      const savedAIMessageId = await createChatMessage(newMessage);
      if (savedAIMessageId) {
        setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        flushRemainder();
        newMessage.content = finalText;
        if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
        try {
          if (finalText?.trim()) {
            const savedAIMessageId = await createChatMessage(newMessage);
            if (savedAIMessageId) {
              setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
            }
          }
        } catch { }
      } else {
        message.error(t('chat.noTokens'));
        setMessages((prev) => prev.filter((m) => m.id !== 'temp-id'));
      }
    } finally {
      if (!chatRoom?.id && newChat) setChatRoom(newChat);
      clearTimeout(timeoutId);
      setLoading(false);
      setIsAsking(false);
    }
  };

  const handleRegenerateAsk = async (prompt: string, fallbackChatRoomId?: string, regenerateMessageId?: string) => {
    if (isAsking) return;
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    setLoading(true);
    setIsAsking(true);
    const controller = new AbortController();
    setAskController(controller);
    const user = await getUser();
    const clientId = user?.clientId;
    if (!clientId) {
      setLoading(false);
      setIsAsking(false);
      return message.error(t('chat.noClientIdFound'));
    }
    const temps = [0.7, 0.8, 0.9, 1];
    const temperature = temps[Math.floor(Math.random() * temps.length)];
    const effectiveChatRoomId = chatRoom?.id || fallbackChatRoomId!;
    if (!effectiveChatRoomId) {
      setLoading(false);
      setIsAsking(false);
      return message.error(t('chat.noChatToRegenerate'));
    }
    const askMessage: AskMessage & any = {
      id: '',
      clientId,
      userId: auth.currentUser.uid,
      chatRoomId: effectiveChatRoomId,
      content: prompt,
      model: model!,
      createdAt: Timestamp.now(),
      temperature,
      regenerate: true,
      regenerateMessageId,
      attachments: [],
    };
    let newMessage: ChatMessage & any = {
      id: 'temp-id',
      userId: auth.currentUser.uid,
      chatRoomId: effectiveChatRoomId,
      content: '',
      owner: 'ai',
      model: model!,
      createdAt: Timestamp.now(),
      fromPrompt: false,
      promptId: null,
      rated: false,
      role: 'assistant',
      prompt,
      attachments: [],
    };
    setMessages((prev) => [...prev, newMessage]);
    stickToBottomIfNeeded('smooth');
    const markerStart = '__OUTPUTS_JSON__';
    const markerEnd = '__END__';
    let outputsFromStream: any[] = [];
    let buffer = '';
    let outputsParsed = false;
    let finalText = '';
    const applyText = (t: string) => {
      if (!t) return;
      finalText += t;
      setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, content: m.content + t } : m)));
      throttledStickDuringStream();
    };
    const flushRemainder = () => {
      if (!buffer) return;
      if (!outputsParsed) {
        const sIdx = buffer.indexOf(markerStart);
        if (sIdx === -1) {
          applyText(buffer);
        } else {
          const before = buffer.slice(0, sIdx);
          applyText(before);
          const after = buffer.slice(sIdx + markerStart.length);
          const eIdx = after.indexOf(markerEnd);
          if (eIdx !== -1) {
            const jsonStr = after.slice(0, eIdx);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            const tail = after.slice(eIdx + markerEnd.length);
            if (tail) applyText(tail);
          }
        }
      } else {
        applyText(buffer);
      }
      buffer = '';
    };
    let timeoutId: any;
    try {
      timeoutId = setTimeout(() => {
        controller.abort();
        message.error(t('chat.timedOut'));
      }, STREAM_TIMEOUT_MS);
      await ask(
        askMessage,
        (chunk) => {
          buffer += chunk;
          while (true) {
            if (outputsParsed) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const sIdx = buffer.indexOf(markerStart);
            if (sIdx === -1) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const before = buffer.slice(0, sIdx);
            applyText(before);
            buffer = buffer.slice(sIdx + markerStart.length);
            const eIdx = buffer.indexOf(markerEnd);
            if (eIdx === -1) break;
            const jsonStr = buffer.slice(0, eIdx);
            buffer = buffer.slice(eIdx + markerEnd.length);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            outputsParsed = true;
          }
        },
        controller.signal
      );
      flushRemainder();
      newMessage.content = finalText;
      if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
      const savedAIMessageId = await createChatMessage(newMessage);
      if (savedAIMessageId) {
        setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        flushRemainder();
        newMessage.content = finalText;
        if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
        try {
          if (finalText?.trim()) {
            const savedAIMessageId = await createChatMessage(newMessage);
            if (savedAIMessageId) {
              setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
            }
          }
        } catch { }
      } else {
        message.error(t('chat.noTokens'));
        setMessages((prev) => prev.filter((m) => m.id !== 'temp-id'));
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setIsAsking(false);
    }
  };

  const handleAskFromPrompt = async (prompt: string, promptId: string, modelUsed: string) => {
    if (!auth.currentUser) return message.error(t('chat.noActiveAccount'));
    setLoading(true);
    setIsAsking(true);
    const controller = new AbortController();
    setAskController(controller);
    const user = await getUser();
    const clientId = user?.clientId;
    if (!clientId) {
      setLoading(false);
      setIsAsking(false);
      return message.error(t('chat.noClientIdFound'));
    }
    const askMessage: AskMessage & any = {
      id: '',
      clientId,
      userId: auth.currentUser.uid,
      chatRoomId: chatRoom?.id!,
      content: prompt,
      model: modelUsed,
      createdAt: Timestamp.now(),
      temperature: 0.7,
      regenerate: false,
      attachments: [],
    };
    let newMessage: ChatMessage & any = {
      id: 'temp-id',
      userId: auth.currentUser.uid,
      chatRoomId: chatRoom?.id!,
      content: '',
      owner: 'ai',
      model: modelUsed,
      createdAt: Timestamp.now(),
      fromPrompt: true,
      promptId,
      rated: false,
      role: 'assistant',
      prompt,
      attachments: [],
    };
    setMessages((prev) => [...prev, newMessage]);
    stickToBottomIfNeeded('smooth');
    const markerStart = '__OUTPUTS_JSON__';
    const markerEnd = '__END__';
    let outputsFromStream: any[] = [];
    let buffer = '';
    let outputsParsed = false;
    let finalText = '';
    const applyText = (t: string) => {
      if (!t) return;
      finalText += t;
      setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, content: m.content + t } : m)));
      throttledStickDuringStream();
    };
    const flushRemainder = () => {
      if (!buffer) return;
      if (!outputsParsed) {
        const sIdx = buffer.indexOf(markerStart);
        if (sIdx === -1) {
          applyText(buffer);
        } else {
          const before = buffer.slice(0, sIdx);
          applyText(before);
          const after = buffer.slice(sIdx + markerStart.length);
          const eIdx = after.indexOf(markerEnd);
          if (eIdx !== -1) {
            const jsonStr = after.slice(0, eIdx);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            const tail = after.slice(eIdx + markerEnd.length);
            if (tail) applyText(tail);
          }
        }
      } else {
        applyText(buffer);
      }
      buffer = '';
    };
    let timeoutId: any;
    try {
      timeoutId = setTimeout(() => {
        controller.abort();
        message.error(t('chat.timedOut'));
      }, STREAM_TIMEOUT_MS);
      await ask(
        askMessage,
        (chunk) => {
          buffer += chunk;
          while (true) {
            if (outputsParsed) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const sIdx = buffer.indexOf(markerStart);
            if (sIdx === -1) {
              applyText(buffer);
              buffer = '';
              break;
            }
            const before = buffer.slice(0, sIdx);
            applyText(before);
            buffer = buffer.slice(sIdx + markerStart.length);
            const eIdx = buffer.indexOf(markerEnd);
            if (eIdx === -1) break;
            const jsonStr = buffer.slice(0, eIdx);
            buffer = buffer.slice(eIdx + markerEnd.length);
            try {
              const parsed = JSON.parse(jsonStr);
              if (Array.isArray(parsed)) outputsFromStream = parsed;
            } catch { }
            outputsParsed = true;
          }
        },
        controller.signal
      );
      flushRemainder();
      newMessage.content = finalText;
      if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
      const savedAIMessageId = await createChatMessage(newMessage);
      if (savedAIMessageId) {
        setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        flushRemainder();
        newMessage.content = finalText;
        if (outputsFromStream?.length) (newMessage as any).outputs = outputsFromStream;
        try {
          if (finalText?.trim()) {
            const savedAIMessageId = await createChatMessage(newMessage);
            if (savedAIMessageId) {
              setMessages((prev) => prev.map((m) => (m.id === 'temp-id' ? { ...m, id: savedAIMessageId } : m)));
            }
          }
        } catch { }
      } else {
        message.error(t('chat.noTokens'));
        setMessages((prev) => prev.filter((m) => m.id !== 'temp-id'));
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
      setIsAsking(false);
    }
  };

  const handleStopAsk = () => {
    askController.abort();
    setIsAsking(false);
    setLoading(false);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success(t('chat.copiedClipboard'));
  };

  const renderAttachmentChips = () => {
    if (!attachments.length) return null;
    return (
      <Row gutter={[6, 6]} wrap style={{ marginBottom: 8 }}>
        {attachments.map((att, idx) => (
          <Col key={`${att.name}_${idx}`}>
            <Tag
              closable
              onClose={(e) => {
                e.preventDefault();
                removeAttachment(idx);
              }}
              closeIcon={<DeleteOutlined />}
            >
              <Space size={6} align="center">
                {guessIcon(att.contentType)}
                <span style={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {att.name}
                </span>
                {att.uploading && <Spin size="small" />}
              </Space>
            </Tag>
          </Col>
        ))}
      </Row>
    );
  };

  const renderMessageAttachments = (msg: any) => {
    const outputsAsAtts = (msg.outputs || [])
      .filter((f: any) => !!f.downloadURL)
      .map((f: any) => ({ name: f.filename || 'archivo', contentType: f.contentType || 'application/octet-stream', downloadURL: f.downloadURL }));
    const atts: any[] = [...(msg.attachments || []), ...outputsAsAtts];
    if (!atts.length) return null;
    return (
      <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
        {atts.map((a, i) => {
          const ct = (a.contentType || '').toLowerCase();
          const isImg = ct.startsWith('image/');
          return (
            <Col key={i} style={{ maxWidth: '100%', overflow: 'hidden' }}>
              {isImg ? (
                <a href={a.downloadURL} target="_blank" rel="noreferrer" style={{ display: 'block', maxWidth: '100%' }}>
                  <img
                    src={a.downloadURL}
                    alt={a.name || 'image'}
                    loading="lazy"
                    style={{ display: 'block', maxWidth: '100%', height: 'auto', maxHeight: '60vh', objectFit: 'contain', borderRadius: 8 }}
                  />
                </a>
              ) : (
                <a href={a.downloadURL} target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
                  {guessIcon(ct)}
                  <span style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                  <DownloadOutlined />
                </a>
              )}
            </Col>
          );
        })}
      </Row>
    );
  };

  const modelMenu = { items: aiModels.map((m) => ({ key: m.value, label: m.label })), onClick: ({ key }: { key: string }) => setModel(key) };

  const getModelTagColor = (model?: string, fallback?: string) => {
    const m = String(model || '').toLowerCase();
    if (m.includes('meta') || m.includes('llama')) return '#ff4d8d';
    if (m.includes('claude')) return '#fa8c16';
    if (m.includes('chatgpt') || m.includes('gpt')) return '#52c41a';
    if (m.includes('mou')) return '#40a9ff';
    return fallback || '#d9d9d9';
  };

  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const bottomInset = composerHeight + 16;

  const commonContainerStyle: React.CSSProperties = {
    backgroundColor: token.colorBgElevated,
    borderTop: `1px solid ${token.colorBorder}`,
    boxShadow: token.boxShadowSecondary,
    padding: 10,
    paddingBottom: 'calc(env(safe-area-inset-bottom) + 10px)',
    willChange: 'transform',
    transform: 'translateZ(0)',
    contain: 'paint',
  };

  const mobileSpecificStyle: React.CSSProperties = {
    position: 'fixed',
    left: 0,
    right: 0,
    bottom: Math.max(kbOverlap, 0),
    zIndex: 400,
  };



  const containerStyle = isMobile
    ? { ...commonContainerStyle, ...mobileSpecificStyle }
    : commonContainerStyle;

  return (
    <Layout style={{
      display: "flex",
      flexDirection: "column",
      height: "var(--vvh, 100dvh)",
      overflow: "hidden",
      overscrollBehavior: "none",
      paddingLeft: isMobile ? 15 : 0,
      paddingRight: isMobile ? 15 : 0,
    }}>
      <div style={{
        height: headerHeight,
        flexShrink: 0,
        position: "relative",
      }}>
        <AppHeader
          position={isMobile ? "sticky" : "fixed"}
          renderModelSelector
          modelMenu={modelMenu as any}
          modelLabel={model || t('chat.selectModel')}
          modelColor={getModelTagColor(model || "", "#57d66a")}
          disabled={isAsking}
          onHeightChange={setHeaderHeight}
        />
      </div>

      <Content
        style={{
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          flex: 1,
          paddingTop: /android/i.test(navigator.userAgent) || !isMobile ? "0px" : "60px"
          // paddingTop: isMobile ? "60px" : "0px"
        }}
      >
        <Row justify="center" style={{ flex: 1, minHeight: 0 }}>
          <Col
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: "100%",
            }}
          >
            <div
              ref={chatContainerRef}
              style={{
                maxWidth: 900,
                margin: "0 auto",
                width: "100%",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                height: "100%",
              }}
            >
              <div
                ref={scrollRef}
                onScroll={handleScroll}
                style={{
                  height: /android/i.test(navigator.userAgent)
                    ? `calc(var(--vvh, 100dvh) - ${headerHeight}px)`
                    : '100%',
                  overflowY: isEmpty ? "hidden" : "auto",
                  WebkitOverflowScrolling: "touch",
                  paddingTop: 1,
                  paddingBottom: bottomInset,
                  maxWidth: 900,
                  margin: "0 auto",
                  width: "100%",
                  overscrollBehavior: "contain",
                  scrollPaddingBottom: bottomInset,
                }}
              >
                {!messages || messages.length === 0 ? (
                  <div
                    style={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      padding: 24,
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{ fontWeight: 500, fontSize: 25 }}
                    >
                      {t("chat.whatDoYouHaveInMind")}
                    </Text>
                  </div>
                ) : (
                  <Space direction="vertical" style={{ width: "100%" }}>
                    {messages.map((msg, idx) => {
                      const isAI = msg.owner === "ai";
                      const isLast = idx === messages.length - 1;
                      const isFirts = idx === 0;
                      const tagColor = isAI
                        ? getModelTagColor(msg.model, token.colorPrimary)
                        : undefined;
                      return (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            justifyContent: isAI ? "flex-start" : "flex-end",
                            width: "100%",
                            marginBottom: isLast ? 24 : 0,
                          }}
                        >
                          {isAI ? (
                            <div
                              style={{
                                width: "100%",
                                marginBottom: isMobile ? '1.5rem' : 0,
                              }}
                            >
                              <div style={{ wordBreak: "break-word" }}>
                                <ReactMarkdown
                                  components={{
                                    a: ({ href, children, ...props }) => {
                                      const u = String(href || "");
                                      const looksImage =
                                        /\.(png|jpg|jpeg|gif|webp)$/i.test(u) ||
                                        /alt=media/i.test(u);
                                      return (
                                        <span
                                          style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 8,
                                            flexWrap: "wrap",
                                          }}
                                        >
                                          <a
                                            {...props}
                                            href={u}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            {children}
                                          </a>
                                          <a
                                            href={u}
                                            target="_blank"
                                            rel="noreferrer"
                                            download
                                            style={{ display: "inline-flex" }}
                                          >
                                            <DownloadOutlined />
                                          </a>
                                          {looksImage && (
                                            <a
                                              href={u}
                                              target="_blank"
                                              rel="noreferrer"
                                              style={{
                                                display: "block",
                                                width: "100%",
                                              }}
                                            >
                                              <img
                                                src={u}
                                                alt={
                                                  typeof children === "string"
                                                    ? children.toString()
                                                    : "imagen"
                                                }
                                              />
                                            </a>
                                          )}
                                        </span>
                                      );
                                    },
                                    img: ({ src, alt }) => (
                                      <a
                                        href={String(src || "")}
                                        target="_blank"
                                        rel="noreferrer"
                                      >
                                        <img
                                          src={String(src || "")}
                                          alt={String(alt || "")}
                                        />
                                      </a>
                                    ),
                                  }}
                                >
                                  {msg.content}
                                </ReactMarkdown>
                              </div>

                              {(msg as any)?.outputs?.length ? (
                                <Card style={{ marginTop: 8 }}>
                                  <Text strong>{t('chat.generatedFiles')}</Text>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 8,
                                      marginTop: 8,
                                    }}
                                  >
                                    {(msg as any).outputs.map(
                                      (f: any, i: number) => (
                                        <Button
                                          key={`${f.downloadURL}-${i}`}
                                          icon={<DownloadOutlined />}
                                          href={f.downloadURL}
                                          target="_blank"
                                          rel="noreferrer"
                                        >
                                          {f.filename ||
                                            (f.contentType?.includes("image")
                                              ? t('chat.image')
                                              : t('chat.file'))}
                                        </Button>
                                      )
                                    )}
                                  </div>
                                </Card>
                              ) : null}

                              {renderMessageAttachments(msg)}

                              {msg.id === "temp-id" ? (
                                <Space size={8} style={{ marginTop: 4 }}>
                                  <Spin size="small" />
                                  <Text type="secondary">{t("chat.generating")}</Text>
                                </Space>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    marginTop: 8,
                                  }}
                                >
                                  <Space size={8} wrap>
                                    {msg.fromPrompt && msg.id !== "temp-id" && (
                                      <Tooltip
                                        title={
                                          msg.rated ? t('chat.rated') : t('chat.rate')
                                        }
                                      >
                                        <Button
                                          icon={
                                            msg.rated ? (
                                              <StarFilled />
                                            ) : (
                                              <StarOutlined />
                                            )
                                          }
                                          onClick={() =>
                                            !msg.rated
                                              ? openRateModal(msg.id)
                                              : message.info(
                                                t('chat.promptAlreadyRated')
                                              )
                                          }
                                        />
                                      </Tooltip>
                                    )}
                                    <Tooltip title={t('chat.regenerate')}>
                                      <Button
                                        icon={<RedoOutlined />}
                                        onClick={() =>
                                          handleRegenerateAsk(
                                            msg.owner === "user"
                                              ? msg.content
                                              : msg.prompt || msg.content,
                                            msg.chatRoomId,
                                            msg.id
                                          )
                                        }
                                      />
                                    </Tooltip>
                                    <Tooltip title={t('chat.copy')}>
                                      <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopy(msg.content)}
                                      />
                                    </Tooltip>
                                  </Space>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      width: 1,
                                      height: 16,
                                      background: token.colorBorder,
                                      margin: "0 8px",
                                    }}
                                  />
                                  <Tag color={tagColor}>{msg.model}</Tag>
                                  <Text type="secondary">
                                    {convertTimestampToDate(
                                      msg.createdAt as Timestamp
                                    )}
                                  </Text>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div
                              style={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: !isFirts ? 0 : "1rem",
                              }}
                            >

                              <div
                                style={{
                                  maxWidth: "80%",
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "flex-end",
                                  gap: 6,
                                }}
                              >
                                <Card
                                  style={{
                                    width: "fit-content",
                                    maxWidth: "100%",
                                    alignSelf: "flex-end",
                                    display: "inline-block",
                                  }}
                                  styles={{
                                    body: {
                                      paddingTop: "0px",
                                      paddingBottom: "0px",
                                      paddingLeft: "8px",
                                      paddingRight: "8px",
                                    },
                                  }}
                                >
                                  <div style={{ wordBreak: "break-word" }}>
                                    <ReactMarkdown
                                      components={{
                                        a: ({ href, children, ...props }) => {
                                          const u = String(href || "");
                                          const looksImage =
                                            /\.(png|jpg|jpeg|gif|webp)$/i.test(
                                              u
                                            ) || /alt=media/i.test(u);
                                          return (
                                            <span
                                              style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 8,
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              <a
                                                {...props}
                                                href={u}
                                                target="_blank"
                                                rel="noreferrer"
                                              >
                                                {children}
                                              </a>
                                              <a
                                                href={u}
                                                target="_blank"
                                                rel="noreferrer"
                                                download
                                                style={{
                                                  display: "inline-flex",
                                                }}
                                              >
                                                <DownloadOutlined />
                                              </a>
                                              {looksImage && (
                                                <a
                                                  href={u}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  style={{
                                                    display: "block",
                                                    width: "100%",
                                                  }}
                                                >
                                                  <img
                                                    src={u}
                                                    alt={
                                                      typeof children ===
                                                        "string"
                                                        ? children.toString()
                                                        : "imagen"
                                                    }
                                                  />
                                                </a>
                                              )}
                                            </span>
                                          );
                                        },
                                        img: ({ src, alt }) => (
                                          <a
                                            href={String(src || "")}
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                            <img
                                              src={String(src || "")}
                                              alt={String(alt || "")}
                                            />
                                          </a>
                                        ),
                                      }}
                                    >
                                      {msg.content}
                                    </ReactMarkdown>
                                  </div>
                                  {renderMessageAttachments(msg)}
                                </Card>

                                {msg.id !== "temp-id" && (
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 8,
                                      flexWrap: "wrap",
                                      alignSelf: "flex-end",
                                    }}
                                  >
                                    <Tooltip title={t('chat.copy')}>
                                      <Button
                                        icon={<CopyOutlined />}
                                        onClick={() => handleCopy(msg.content)}
                                      />
                                    </Tooltip>
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 1,
                                        height: 16,
                                        background: token.colorBorder,
                                        margin: "0 8px",
                                      }}
                                    />
                                    <Text type="secondary">
                                      {convertTimestampToDate(
                                        msg.createdAt as Timestamp
                                      )}
                                    </Text>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </Space>
                )}
                <div
                  ref={bottomRef}
                  style={{ height: 1, scrollMarginBottom: bottomInset }}
                />
              </div>
            </div>
          </Col>
        </Row>

        {/* {!isAtBottom && messages?.length > 0 && (
          <FloatButton
            icon={<ArrowDownOutlined />}
            onClick={() => {
              autoStickRef.current = true;
              scrollToBottom("smooth");
            }}
            style={{
              position: "fixed",
              left: composerBox.width
                ? `${composerBox.left + composerBox.width / 2}px`
                : "50%",
              transform: "translateX(-50%)",
              bottom: bottomInset,
              zIndex: 2000,
            }}
          />
        )} */}
      </Content>

      <div ref={composerRef} style={containerStyle}>
        {renderAttachmentChips()}
        <Row justify="center" style={{ paddingBottom: 15 }}>
          <Col style={{ width: "100%", maxWidth: 900 }}>
            <Row
              gutter={8}
              wrap={false}
              align="middle"
              style={{ touchAction: "manipulation" }}
            >
              <Col flex="none">
                <Tooltip title={t("chat.attachFile")}>
                  <Button
                    icon={<PlusOutlined />}
                    onClick={handleAttachClick}
                    style={{ touchAction: "manipulation" }}
                  />
                </Tooltip>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT}
                  multiple
                  style={{ display: "none" }}
                  onChange={onFilesChosen}
                />
              </Col>
              <Col
                flex="auto"
                style={{
                  display: "flex",
                  alignItems: "center",
                  minWidth: 0,
                  touchAction: "manipulation",
                }}
              >
                <Input.TextArea
                  ref={textareaRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={
                    isUploading ? t("chat.uploadingFiles") : t("chat.typeMessage")
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey && !isAsking) {
                      setIsAsking(true)
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  onFocus={() => {
                    autoStickRef.current = true;
                    scrollToBottom("smooth");
                  }}
                  onTouchMove={(e) => {
                    if (isMobile) {
                      e.preventDefault();
                    }
                  }}
                  disabled={isUploading || isAsking}
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  style={{
                    resize: "none",
                    overflowY: "auto",
                    fontSize: 16,
                    borderRadius: textLines === 1 ? 9999 : 10,
                    lineHeight: 1.4,
                    padding: "8px 12px",
                    paddingRight: 14,
                    scrollbarGutter: "stable both-edges",
                    backgroundColor: token.colorBgContainer,
                    border: `1px solid ${token.colorBorder}`,
                    boxShadow: "none",
                    backgroundClip: "padding-box",
                    touchAction: "none",
                  }}
                  enterKeyHint="send"
                />

              </Col>
              <Col flex="none">
                {isAsking ? (
                  <Tooltip title={t('chat.stop')}>
                    <Button
                      danger
                      icon={<StopOutlined />}
                      onClick={handleStopAsk}
                      aria-label={t('chat.stop')}
                      style={{ touchAction: "manipulation" }}
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title={t("common.send")}>
                    <Button
                      type="primary"
                      icon={<ArrowUpOutlined />}
                      onClick={handleSendMessage}
                      aria-label={t('common.send')}
                      disabled={
                        (!inputValue.trim() && attachments.length === 0) ||
                        isUploading
                      }
                      style={{ touchAction: "manipulation" }}
                    />
                  </Tooltip>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      <Modal
        title={t('chat.ratePromptTitle')}
        open={isRateModalVisible}
        onCancel={() => setIsRateModalVisible(false)}
        onOk={handleRatePrompt}
        okText={t('chat.submit')}
        confirmLoading={loading}
        centered
      >
        <Spin spinning={loading}>
          <Rate onChange={setCurrentRate} value={currentRate} />
        </Spin>
      </Modal>
    </Layout>
  );
};

export default ChatPageContent;
